import React, { useState, useEffect } from 'react';
import { FaClock, FaTrophy, FaCheck, FaTimes, FaTrash, FaGamepad, FaArrowLeft, FaUsers, FaCoins } from 'react-icons/fa';
import { apiService } from '../services/apiService';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface Challenge {
  id: number;
  gameId: number;
  gameName: string;
  creatorId: number;
  creatorUsername: string;
  creatorAvatar: string;
  opponentId?: number;
  opponentUsername?: string;
  opponentAvatar?: string;
  stake: number;
  status: string;
  multiSigWalletId?: number;
  multiSigWalletAddress?: string;
  isOpenChallenge: boolean;
  createdAt: string;
  expiresAt: string;
  timeRemaining: number; // in seconds
}

const PendingChallenges: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'received' | 'sent' | 'open' | 'history'>('received');
  const [receivedChallenges, setReceivedChallenges] = useState<Challenge[]>([]);
  const [sentChallenges, setSentChallenges] = useState<Challenge[]>([]);
  const [openChallenges, setOpenChallenges] = useState<Challenge[]>([]);
  const [historyChallenges, setHistoryChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchChallenges();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchChallenges, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchChallenges = async () => {
    setLoading(true);
    setError(null);
    try {
      const [receivedRes, sentRes, openRes, historyRes] = await Promise.all([
        apiService.get('/api/p2p-games/challenges/pending/received'),
        apiService.get('/api/p2p-games/challenges/pending/sent'),
        apiService.get('/api/p2p-games/challenges/open'),
        apiService.get('/api/p2p-games/challenges/history')
      ]);

      if (receivedRes.success) setReceivedChallenges(receivedRes.data);
      if (sentRes.success) setSentChallenges(sentRes.data);
      if (openRes.success) setOpenChallenges(openRes.data);
      if (historyRes.success) setHistoryChallenges(historyRes.data);
    } catch (err: any) {
      console.error('Error fetching challenges:', err);
      setError(err.message || 'Failed to load challenges');
    } finally {
      setLoading(false);
    }
  };

  const acceptChallenge = async (challengeId: number) => {
    setActionLoading(challengeId);
    try {
      const response = await apiService.post('/api/p2p-games/challenges/respond', {
        challengeId,
        action: 'ACCEPT'
      });

      if (response.success) {
        toast.success('Challenge accepted! Entering game lobby...');
        // Navigate to game lobby
        setTimeout(() => {
          navigate('/p2p-game/lobby/' + challengeId);
        }, 1000);
      } else {
        toast.error('Failed to accept challenge');
      }
    } catch (err: any) {
      console.error('Error accepting challenge:', err);
      toast.error(err.message || 'Failed to accept challenge');
    } finally {
      setActionLoading(null);
      fetchChallenges();
    }
  };

  const declineChallenge = async (challengeId: number) => {
    setActionLoading(challengeId);
    try {
      const response = await apiService.post('/api/p2p-games/challenges/respond', {
        challengeId,
        action: 'DECLINE'
      });

      if (response.success) {
        toast.success('Challenge declined');
        fetchChallenges();
      } else {
        toast.error('Failed to decline challenge');
      }
    } catch (err: any) {
      console.error('Error declining challenge:', err);
      toast.error(err.message || 'Failed to decline challenge');
    } finally {
      setActionLoading(null);
    }
  };

  const cancelChallenge = async (challengeId: number) => {
    setActionLoading(challengeId);
    try {
      const response = await apiService.delete(`/api/p2p-games/challenges/${challengeId}/cancel`);

      if (response.success) {
        toast.success('Challenge cancelled and stake refunded');
        fetchChallenges();
      } else {
        toast.error('Failed to cancel challenge');
      }
    } catch (err: any) {
      console.error('Error cancelling challenge:', err);
      toast.error(err.message || 'Failed to cancel challenge');
    } finally {
      setActionLoading(null);
    }
  };

  const formatTimeRemaining = (seconds: number): string => {
    if (seconds <= 0) return 'Expired';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    if (minutes > 0) return `${minutes}m remaining`;
    return `${seconds}s remaining`;
  };

  const renderChallenge = (challenge: Challenge, type: 'received' | 'sent' | 'open' | 'history') => {
    const isHistory = type === 'history';
    const statusColor = getStatusBadgeColor(challenge.status);
    
    return (
      <div
        key={challenge.id}
        style={{
          background: 'white',
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          padding: isMobile ? 16 : 20,
          transition: 'all 0.3s ease',
          width: '100%',
          boxSizing: 'border-box'
        }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
      >
        {/* Challenge Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          marginBottom: 16,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 12 : 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 20
            }}>
              <FaGamepad />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ 
                fontSize: isMobile ? 14 : 16, 
                fontWeight: 700, 
                color: '#1a202c', 
                marginBottom: 4,
                wordBreak: 'break-word'
              }}>
                {challenge.gameName}
              </h3>
              <div style={{ fontSize: isMobile ? 11 : 12, color: '#718096' }}>
                {type === 'sent' ? (
                  challenge.isOpenChallenge ? (
                    <span>Open Challenge • Waiting for opponent</span>
                  ) : (
                    <span>Challenge to {challenge.opponentUsername}</span>
                  )
                ) : (
                  <span>Challenge from {challenge.creatorUsername}</span>
                )}
              </div>
            </div>
          </div>

          {/* Status Badge (for history) or Time Remaining (for pending) */}
          {isHistory ? (
            <div style={{
              padding: isMobile ? '4px 8px' : '6px 12px',
              background: statusColor + '20',
              color: statusColor,
              borderRadius: 6,
              fontSize: isMobile ? 10 : 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}>
              {challenge.status}
            </div>
          ) : (
            <div style={{
              padding: isMobile ? '4px 8px' : '6px 12px',
              background: challenge.timeRemaining < 3600 ? '#fed7d7' : '#e6fffa',
              color: challenge.timeRemaining < 3600 ? '#c53030' : '#047857',
              borderRadius: 6,
              fontSize: isMobile ? 10 : 12,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}>
              <FaClock />
              {formatTimeRemaining(challenge.timeRemaining)}
            </div>
          )}
        </div>

        {/* Challenge Details */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 12,
          padding: isMobile ? 12 : 16,
          background: '#f7fafc',
          borderRadius: 8,
          marginBottom: 16
        }}>
          <div>
            <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>
              Stake Amount
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1a202c', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaCoins style={{ color: '#f6ad55' }} />
              €{challenge.stake.toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#718096', marginBottom: 4 }}>
              Potential Prize
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#48bb78' }}>
              €{(challenge.stake * 1.9).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ 
          display: 'flex', 
          gap: isMobile ? 8 : 12,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          {isHistory && challenge.status === 'ACCEPTED' && (
            <button
              onClick={() => navigate('/p2p-game/lobby/' + challenge.id)}
              style={{
                flex: 1,
                padding: isMobile ? '10px 12px' : '10px 16px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: isMobile ? 12 : 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              <FaGamepad />
              {isMobile ? 'View Lobby' : 'View Game Lobby'}
            </button>
          )}
          
          {type === 'received' && challenge.status === 'PENDING' && (
            <>
              <button
                onClick={() => acceptChallenge(challenge.id)}
                disabled={actionLoading === challenge.id}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 12px' : '10px 16px',
                  background: '#48bb78',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: isMobile ? 12 : 14,
                  fontWeight: 600,
                  cursor: actionLoading === challenge.id ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  opacity: actionLoading === challenge.id ? 0.7 : 1
                }}
              >
                {actionLoading === challenge.id ? (
                  <div style={{ width: 16, height: 16, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                ) : (
                  <>
                    <FaCheck />
                    {isMobile ? 'Accept' : 'Accept Challenge'}
                  </>
                )}
              </button>
              <button
                onClick={() => declineChallenge(challenge.id)}
                disabled={actionLoading === challenge.id}
                style={{
                  flex: 1,
                  padding: isMobile ? '10px 12px' : '10px 16px',
                  background: '#fed7d7',
                  color: '#c53030',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: isMobile ? 12 : 14,
                  fontWeight: 600,
                  cursor: actionLoading === challenge.id ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  opacity: actionLoading === challenge.id ? 0.7 : 1
                }}
              >
                <FaTimes />
                Decline
              </button>
            </>
          )}

          {type === 'sent' && challenge.status === 'PENDING' && (
            <button
              onClick={() => cancelChallenge(challenge.id)}
              disabled={actionLoading === challenge.id}
              style={{
                width: '100%',
                padding: isMobile ? '10px 12px' : '10px 16px',
                background: '#fed7d7',
                color: '#c53030',
                border: 'none',
                borderRadius: 8,
                fontSize: isMobile ? 12 : 14,
                fontWeight: 600,
                cursor: actionLoading === challenge.id ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: actionLoading === challenge.id ? 0.7 : 1
              }}
            >
              {actionLoading === challenge.id ? (
                <div style={{ width: 16, height: 16, border: '2px solid #c53030', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : (
                <>
                  <FaTrash />
                  {isMobile ? 'Cancel' : 'Cancel Challenge'}
                </>
              )}
            </button>
          )}

          {type === 'open' && challenge.status === 'PENDING' && (
            <button
              onClick={() => acceptChallenge(challenge.id)}
              disabled={actionLoading === challenge.id}
              style={{
                width: '100%',
                padding: isMobile ? '10px 12px' : '10px 16px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                fontSize: isMobile ? 12 : 14,
                fontWeight: 600,
                cursor: actionLoading === challenge.id ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                opacity: actionLoading === challenge.id ? 0.7 : 1
              }}
            >
              {actionLoading === challenge.id ? (
                <div style={{ width: 16, height: 16, border: '2px solid white', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              ) : (
                <>
                  <FaTrophy />
                  {isMobile ? 'Accept' : 'Accept Challenge'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  const getCurrentChallenges = () => {
    switch (activeTab) {
      case 'received':
        return receivedChallenges;
      case 'sent':
        return sentChallenges;
      case 'open':
        return openChallenges;
      case 'history':
        return historyChallenges;
      default:
        return [];
    }
  };
  
  const getStatusBadgeColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PENDING':
        return '#f6ad55'; // orange
      case 'ACCEPTED':
        return '#48bb78'; // green
      case 'DECLINED':
        return '#fc8181'; // red
      case 'CANCELLED':
        return '#a0aec0'; // gray
      case 'EXPIRED':
        return '#718096'; // dark gray
      case 'COMPLETED':
        return '#667eea'; // purple
      default:
        return '#a0aec0';
    }
  };

  return (
    <div style={{ 
      maxWidth: 1200, 
      margin: '0 auto', 
      padding: isMobile ? '16px' : '24px',
      width: '100%',
      boxSizing: 'border-box',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div style={{ marginBottom: isMobile ? 24 : 32 }}>
        <button
          onClick={() => navigate('/p2p-games')}
          style={{
            padding: isMobile ? '6px 12px' : '8px 16px',
            background: 'transparent',
            color: '#667eea',
            border: '1px solid #667eea',
            borderRadius: 8,
            fontSize: isMobile ? 12 : 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16
          }}
        >
          <FaArrowLeft />
          {isMobile ? 'Back' : 'Back to Games'}
        </button>

        <h1 style={{ 
          fontSize: isMobile ? 24 : 32, 
          fontWeight: 700, 
          color: '#1a202c', 
          marginBottom: 8, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 12,
          flexWrap: 'wrap'
        }}>
          <FaTrophy style={{ color: '#667eea' }} />
          My Challenges
        </h1>
        <p style={{ fontSize: isMobile ? 14 : 16, color: '#718096' }}>
          Manage your pending game challenges
        </p>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: isMobile ? 8 : 16, 
        marginBottom: 24, 
        borderBottom: '2px solid #e2e8f0',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        {[
          { id: 'received', label: 'Received', count: receivedChallenges.length },
          { id: 'sent', label: 'Sent', count: sentChallenges.length },
          { id: 'open', label: 'Open Challenges', count: openChallenges.length },
          { id: 'history', label: 'History', count: historyChallenges.length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            style={{
              padding: isMobile ? '10px 12px' : '12px 24px',
              background: 'transparent',
              color: activeTab === tab.id ? '#667eea' : '#718096',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #667eea' : '3px solid transparent',
              marginBottom: -2,
              fontSize: isMobile ? 12 : 14,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 4 : 8,
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {tab.label}
            <span style={{
              background: activeTab === tab.id ? '#667eea' : '#e2e8f0',
              color: activeTab === tab.id ? 'white' : '#718096',
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 700
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Error State */}
      {error && (
        <div style={{
          padding: 16,
          background: '#fed7d7',
          borderRadius: 8,
          color: '#c53030',
          marginBottom: 24
        }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{
            width: 50,
            height: 50,
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}

      {/* Challenges List */}
      {!loading && !error && (
        <div>
          {getCurrentChallenges().length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: 60,
              background: 'white',
              borderRadius: 12,
              border: '1px solid #e2e8f0'
            }}>
              <FaTrophy style={{ fontSize: 48, color: '#cbd5e0', marginBottom: 16 }} />
              <h3 style={{ fontSize: 18, fontWeight: 600, color: '#4a5568', marginBottom: 8 }}>
                No challenges yet
              </h3>
              <p style={{ color: '#718096', marginBottom: 20 }}>
                {activeTab === 'received' && 'You have no pending challenges from other players'}
                {activeTab === 'sent' && 'You haven\'t sent any challenges yet'}
                {activeTab === 'open' && 'No open challenges available at the moment'}
                {activeTab === 'history' && 'No challenge history yet'}
              </p>
              <button
                onClick={() => navigate('/p2p-games')}
                style={{
                  padding: isMobile ? '10px 16px' : '10px 20px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: isMobile ? 12 : 14,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Browse Games
              </button>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {getCurrentChallenges().map(challenge => renderChallenge(challenge, activeTab))}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        /* Hide scrollbar for tabs */
        div[style*="overflowX"]::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default PendingChallenges;

